// src/components/world/Primitives.jsx
// 3D-примитивы для локаций и аватара. Минимум геометрии — максимум совместимости.

import React, { useMemo } from 'react';
import { Html } from '@react-three/drei';
import * as THREE from 'three';

// ============================================================================
// ЦВЕТА ПО ТЕМПЕРАТУРЕ (для планет)
// ============================================================================
function colorByTemperature(temp) {
  if (typeof temp !== 'number') return '#4a7fb5';
  if (temp < -50) return '#a8d4ff';
  if (temp < 0) return '#6aa0d8';
  if (temp < 20) return '#4a7fb5';
  if (temp < 40) return '#c8b46a';
  if (temp < 80) return '#d87a3a';
  return '#e63a2a';
}

// ============================================================================
// LABEL (HTML-метка над объектом)
// ============================================================================
export function Label({ text, position = [0, 0, 0], color = '#00e5ff', fontSize = 12 }) {
  if (!text) return null;
  return (
    <Html position={position} center distanceFactor={10} zIndexRange={[10, 0]}>
      <div
        style={{
          color,
          fontSize,
          fontWeight: 600,
          whiteSpace: 'nowrap',
          textShadow: '0 0 6px rgba(0,0,0,0.9), 0 0 3px rgba(0,0,0,0.8)',
          pointerEvents: 'none',
          userSelect: 'none',
          fontFamily: 'system-ui, sans-serif',
          padding: '2px 6px',
          background: 'rgba(0,0,0,0.55)',
          borderRadius: 4,
          border: `1px solid ${color}55`,
        }}
      >
        {text}
      </div>
    </Html>
  );
}

// ============================================================================
// ПЛАНЕТА (Sphere)
// ============================================================================
export function Planet({ position = [0, 0, 0], temperature, name }) {
  const color = useMemo(() => colorByTemperature(temperature), [temperature]);
  return (
    <group position={position}>
      <mesh>
        <sphereGeometry args={[1, 32, 32]} />
        <meshStandardMaterial color={color} roughness={0.85} metalness={0.1} />
      </mesh>
      <Label text={name} position={[0, 1.3, 0]} color="#a8d4ff" />
    </group>
  );
}

// ============================================================================
// СТАНЦИЯ (Torus + сфера в центре)
// ============================================================================
export function Station({ position = [0, 0, 0], name }) {
  return (
    <group position={position}>
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.5, 0.15, 16, 32]} />
        <meshStandardMaterial color="#00e5ff" emissive="#00a0c0" emissiveIntensity={0.5} metalness={0.7} roughness={0.3} />
      </mesh>
      <mesh>
        <sphereGeometry args={[0.2, 16, 16]} />
        <meshStandardMaterial color="#ffffff" emissive="#00e5ff" emissiveIntensity={0.8} />
      </mesh>
      <Label text={name} position={[0, 0.85, 0]} color="#00e5ff" />
    </group>
  );
}

// ============================================================================
// КОРАБЛЬ (Cone)
// ============================================================================
export function Ship({ position = [0, 0, 0], name }) {
  return (
    <group position={position}>
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <coneGeometry args={[0.4, 0.8, 12]} />
        <meshStandardMaterial color="#ff9800" emissive="#ff6f00" emissiveIntensity={0.3} metalness={0.6} roughness={0.4} />
      </mesh>
      <Label text={name} position={[0, 0.65, 0]} color="#ff9800" />
    </group>
  );
}

// ============================================================================
// КОСМОПОРТ (Cylinder)
// ============================================================================
export function Spaceport({ position = [0, 0, 0], name }) {
  return (
    <group position={position}>
      <mesh position={[0, 0.15, 0]}>
        <cylinderGeometry args={[0.6, 0.7, 0.3, 24]} />
        <meshStandardMaterial color="#3b82f6" metalness={0.5} roughness={0.4} />
      </mesh>
      <Label text={name} position={[0, 0.6, 0]} color="#3b82f6" />
    </group>
  );
}

// ============================================================================
// НЕИЗВЕСТНО (Box)
// ============================================================================
export function Unknown({ position = [0, 0, 0], name }) {
  return (
    <group position={position}>
      <mesh position={[0, 0.25, 0]}>
        <boxGeometry args={[0.5, 0.5, 0.5]} />
        <meshStandardMaterial color="#666666" wireframe />
      </mesh>
      <Label text={name || 'Неизвестно'} position={[0, 0.75, 0]} color="#999999" />
    </group>
  );
}

// ============================================================================
// МАРКЕР (для точек на локации, если пригодятся)
// ============================================================================
export function Marker({ position = [0, 0, 0], color = '#00e5ff', label }) {
  return (
    <group position={position}>
      <mesh>
        <sphereGeometry args={[0.1, 12, 12]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={1} />
      </mesh>
      {label && <Label text={label} position={[0, 0.25, 0]} color={color} fontSize={10} />}
    </group>
  );
}
